import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  AddAPhotoOutlined,
  Add,
  Remove,
  Close,
  CheckCircle,
  LibraryAddOutlined,
  HistoryOutlined,
  EventOutlined,
  Replay,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderContext } from "../../contexts/OrderContext";
import {
  ACCENT,
  ACCENT_DARK,
  ACCENT_SOFT,
  FOOTER_SPACE,
  PAGE_BG,
  isVideoUrl,
  parseImages,
} from "./orderUi";

// Local date as YYYY-MM-DD (toISOString would give the UTC date, which is
// yesterday before 5:30 AM IST).
const toLocalIsoDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().split("T")[0];
};

const formatDisplayDate = (isoDate) => isoDate.split("-").reverse().join("-");

const tileSx = {
  position: "relative",
  width: { xs: 76, md: 88 },
  height: { xs: 76, md: 88 },
  flexShrink: 0,
  borderRadius: "8px",
  overflow: "hidden",
  bgcolor: "#f3f3f3",
};

const secondaryButtonSx = {
  bgcolor: "#fff",
  borderColor: "#D0D5DD",
  color: "#344054",
  px: { xs: 1.25, md: 2 },
  py: { xs: 0.5, md: 0.75 },
  fontSize: { xs: 13, md: 14 },
  "& .MuiButton-startIcon": { mr: { xs: 0.5, md: 1 } },
  "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
};

const FieldLabel = ({ children, required, hint }) => (
  <Typography
    variant="body2"
    fontWeight={600}
    sx={{ mb: 0.75, color: "#344054" }}
  >
    {children}
    {required && <span style={{ color: "#DC2626" }}> *</span>}
    {hint && (
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        fontWeight={400}
      >
        {" "}
        ({hint})
      </Typography>
    )}
  </Typography>
);

const RemoveButton = ({ onClick }) => (
  <IconButton
    size="small"
    aria-label="remove photo"
    onClick={onClick}
    sx={{
      position: "absolute",
      top: 4,
      right: 4,
      bgcolor: "rgba(0,0,0,0.55)",
      color: "#fff",
      p: 0.25,
      "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
    }}
  >
    <Close sx={{ fontSize: 16 }} />
  </IconButton>
);

const Thumb = ({ src, video, onRemove }) => (
  <Box sx={tileSx}>
    <Box
      component={video ? "video" : "img"}
      src={src}
      alt=""
      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
    <RemoveButton onClick={onRemove} />
  </Box>
);

// Simplified order screen for customers placing their own orders: only gross
// weight, pcs, notes and photos. The manufacturer fills in the rest later and
// the backend sets the delivery date.
//
// Opened from My Orders with { reorderFrom: order } it repeats that order: the
// visible fields are pre-filled and editable, and the backend copies karigar,
// item, design, purity and size from the original.
const CustomerOrderCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [reorderFrom, setReorderFrom] = useState(
    location.state?.reorderFrom || null,
  );
  const { storeData, fetchOrders, setStoreData } = useContext(OrderContext);
  const token = localStorage.getItem("authToken");
  const { latestOrder } = storeData;

  const mfrId = JSON.parse(localStorage.getItem("selectedMfr"))?.id || "";
  const customerId = localStorage.getItem("id") || "";

  const today = toLocalIsoDate(new Date());

  const [grossWt, setGrossWt] = useState(
    Number(reorderFrom?.weight) > 0 ? String(Number(reorderFrom.weight)) : "",
  );
  const [pcs, setPcs] = useState(Number(reorderFrom?.pcs) || 1);
  const [notes, setNotes] = useState(reorderFrom?.description || "");
  const [files, setFiles] = useState([]);
  // Photos reused from the order being repeated (URLs, not uploads)
  const [keptImages, setKeptImages] = useState(
    parseImages(reorderFrom?.order_images),
  );
  const [loading, setLoading] = useState(false);
  const [grossError, setGrossError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [placedOrderNo, setPlacedOrderNo] = useState(null);

  const previews = useMemo(
    () => files.map((file) => URL.createObjectURL(file)),
    [files],
  );
  useEffect(
    () => () => previews.forEach((url) => URL.revokeObjectURL(url)),
    [previews],
  );

  const handleAddFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selected]);
    e.target.value = ""; // allow picking the same file again
  };

  const handleRemoveFile = (index) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const resetForm = () => {
    setGrossWt("");
    setPcs(1);
    setNotes("");
    setFiles([]);
    setKeptImages([]);
    setGrossError(false);
    setReorderFrom(null);
    // Drop the reorder state so a refresh doesn't bring it back
    navigate(location.pathname, { replace: true, state: null });
  };

  const handleSubmit = async () => {
    if (!grossWt || Number(grossWt) <= 0) {
      setGrossError(true);
      setErrorMessage("Please enter the gross weight");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("orderdate", today);
      data.append("selectedMFR", mfrId);
      data.append("selectedStoreId", localStorage.getItem("store_id") || "");
      data.append("selectedCustmerId", customerId);
      data.append("selectedStatusId", 1);
      data.append("grossWt", grossWt);
      data.append("pcs", pcs);
      data.append("orderDesc", notes.trim());
      data.append("orderType", "Customer");
      if (reorderFrom) {
        data.append("reorderFromId", reorderFrom.orderid);
        data.append("keepImages", JSON.stringify(keptImages));
      }
      files.forEach((file) => data.append("files", file));

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/orders/createorder`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setPlacedOrderNo(response.data.data.storeorderno);
      setStoreData((prev) => ({
        ...prev,
        latestOrder: response.data.nextincrement,
      }));
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error("Error placing order:", error);
      setErrorMessage(
        error.response?.data?.data?.message ||
          "Could not place the order. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const primaryButtonSx = {
    py: 1.5,
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: 700,
    fontSize: 16,
    bgcolor: ACCENT,
    "&:hover": { bgcolor: ACCENT_DARK },
  };

  if (placedOrderNo) {
    return (
      <Box sx={{ bgcolor: PAGE_BG, minHeight: "100%", pb: FOOTER_SPACE }}>
        <Box
          sx={{ maxWidth: 480, mx: "auto", px: 2, pt: 6, textAlign: "center" }}
        >
          <CheckCircle sx={{ fontSize: 80, color: "success.main" }} />
          <Typography variant="h5" fontWeight={700} sx={{ mt: 1 }}>
            Order placed
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Your order number is
          </Typography>
          <Box
            sx={{
              display: "inline-block",
              my: 1.5,
              px: 2.5,
              py: 1,
              borderRadius: "8px",
              bgcolor: ACCENT_SOFT,
              color: ACCENT,
              fontWeight: 800,
              fontSize: 28,
              letterSpacing: 1,
            }}
          >
            {placedOrderNo}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            You'll get a WhatsApp confirmation shortly.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setPlacedOrderNo(null)}
              sx={primaryButtonSx}
            >
              Place another order
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate("/my-orders")}
              sx={{
                py: 1.25,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                color: ACCENT,
                borderColor: ACCENT,
              }}
            >
              View my orders
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: PAGE_BG,
        // Fill the screen between the app bar and the bottom tabs so the
        // action bar sits at the bottom even when the form is short.
        minHeight: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
        display: "flex",
        flexDirection: "column",
        pb: "calc(60px + env(safe-area-inset-bottom))",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          pt: { xs: 1.5, md: 3 },
          pb: { xs: 2, md: 3 },
        }}
      >
        {/* Page header: title left, shortcuts right (one row on phones too) */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: { xs: 1.5, md: 2.5 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              fontWeight={700}
              noWrap
              sx={{ fontSize: { xs: 18, md: 20 }, lineHeight: 1.3 }}
            >
              {reorderFrom ? "Repeat order" : "New order"}
            </Typography>
            <Typography
              color="text.secondary"
              noWrap
              sx={{ fontSize: { xs: 12.5, md: 14 } }}
            >
              <strong>{latestOrder || "-"}</strong> · {formatDisplayDate(today)}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", gap: { xs: 0.75, md: 1 }, flexShrink: 0 }}
          >
            <Button
              variant="outlined"
              startIcon={<HistoryOutlined />}
              onClick={() => navigate("/my-orders")}
              sx={secondaryButtonSx}
            >
              Past
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                &nbsp;orders
              </Box>
            </Button>
            <Button
              variant="outlined"
              startIcon={<LibraryAddOutlined />}
              onClick={() => navigate("/bulkorder")}
              sx={secondaryButtonSx}
            >
              Bulk
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                &nbsp;orders
              </Box>
            </Button>
          </Box>
        </Box>

        {/* Reorder notice */}
        {reorderFrom && (
          <Alert
            severity="info"
            icon={<Replay fontSize="inherit" />}
            action={
              <Button color="inherit" size="small" onClick={resetForm}>
                Cancel
              </Button>
            }
            sx={{
              mb: 2,
              alignItems: "center",
              bgcolor: ACCENT_SOFT,
              color: "#0A4A4D",
              border: "1px solid #CDEBEB",
              "& .MuiAlert-icon": { color: ACCENT },
            }}
          >
            Repeating <strong>{reorderFrom.storeorderno}</strong> with the same
            karigar, item and design. You can change anything below.
          </Alert>
        )}

        {/* Form */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              px: 3,
              py: 2,
              borderBottom: "1px solid #E4E7EC",
            }}
          >
            <Typography fontWeight={700}>Order details</Typography>
            <Typography variant="body2" color="text.secondary">
              The manufacturer adds item, design and the other details after you
              place the order.
            </Typography>
          </Box>

          <Box
            sx={{
              p: { xs: 2, md: 3 },
              display: "grid",
              // Phones: weight + pcs share a row; desktop: two equal columns
              gridTemplateColumns: { xs: "1fr auto", md: "1fr 1fr" },
              gap: { xs: 2, md: 3 },
            }}
          >
            {/* Gross weight */}
            <Box>
              <FieldLabel required>Gross weight</FieldLabel>
              <TextField
                fullWidth
                type="number"
                placeholder="e.g. 12.5"
                value={grossWt}
                onChange={(e) => {
                  setGrossWt(e.target.value);
                  setGrossError(false);
                }}
                error={grossError}
                helperText={grossError ? "Enter the gross weight" : ""}
                inputProps={{
                  inputMode: "decimal",
                  step: "0.001",
                  min: 0,
                  "aria-label": "Gross weight",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">g</InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Pcs */}
            <Box>
              <FieldLabel>Pcs</FieldLabel>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 56,
                  border: "1px solid #D0D5DD",
                  borderRadius: "8px",
                  bgcolor: "#fff",
                }}
              >
                <IconButton
                  aria-label="decrease pcs"
                  onClick={() => setPcs((p) => Math.max(1, p - 1))}
                  disabled={pcs <= 1}
                  sx={{ mx: { xs: 0, md: 0.5 } }}
                >
                  <Remove />
                </IconButton>
                <Box
                  component="input"
                  value={pcs}
                  inputMode="numeric"
                  aria-label="pcs"
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setPcs(Number.isNaN(value) || value < 1 ? 1 : value);
                  }}
                  sx={{
                    width: { xs: 40, md: 56 },
                    height: "100%",
                    border: "none",
                    borderLeft: "1px solid #E4E7EC",
                    borderRight: "1px solid #E4E7EC",
                    outline: "none",
                    textAlign: "center",
                    fontSize: 16,
                    fontWeight: 600,
                    bgcolor: "transparent",
                  }}
                />
                <IconButton
                  aria-label="increase pcs"
                  onClick={() => setPcs((p) => p + 1)}
                  sx={{ mx: { xs: 0, md: 0.5 } }}
                >
                  <Add />
                </IconButton>
              </Box>
            </Box>

            {/* Notes */}
            <Box sx={{ gridColumn: "1 / -1" }}>
              <FieldLabel>Notes</FieldLabel>
              <TextField
                placeholder="Size, colour, design changes, anything for the karigar"
                fullWidth
                multiline
                minRows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                inputProps={{ "aria-label": "Notes" }}
              />
            </Box>

            {/* Photos (shown first on phones) */}
            <Box sx={{ gridColumn: "1 / -1", order: { xs: -1, md: 0 } }}>
              <FieldLabel hint="optional">Reference photos</FieldLabel>
              {keptImages.length + files.length === 0 ? (
                // Nothing added yet: one large, inviting drop zone
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: { xs: 1.75, md: 2.25 },
                    border: "1.5px dashed #9FD3D4",
                    borderRadius: "10px",
                    bgcolor: "#F3FAFA",
                    cursor: "pointer",
                    transition:
                      "background-color 150ms ease, border-color 150ms ease",
                    "&:hover": { bgcolor: "#E9F5F5", borderColor: ACCENT },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: "#fff",
                      color: ACCENT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 3px rgba(16, 24, 40, 0.1)",
                    }}
                  >
                    <AddAPhotoOutlined />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      fontWeight={600}
                      sx={{ fontSize: 14.5, color: "#0A4A4D" }}
                    >
                      Add photos of the design
                    </Typography>
                    <Typography sx={{ fontSize: 12.5 }} color="text.secondary">
                      Take a photo or choose from your gallery
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,video/mp4"
                    multiple
                    hidden
                    onChange={handleAddFiles}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1, md: 1.5 },
                  }}
                >
                  {keptImages.map((url) => (
                    <Thumb
                      key={url}
                      src={url}
                      video={isVideoUrl(url)}
                      onRemove={() =>
                        setKeptImages((prev) => prev.filter((u) => u !== url))
                      }
                    />
                  ))}
                  {previews.map((url, index) => (
                    <Thumb
                      key={url}
                      src={url}
                      video={files[index]?.type.startsWith("video")}
                      onRemove={() => handleRemoveFile(index)}
                    />
                  ))}
                  <Box
                    component="label"
                    sx={{
                      ...tileSx,
                      bgcolor: "#F9FAFB",
                      border: "1px dashed #98A2B3",
                      color: "#475467",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      transition: "border-color 150ms ease, color 150ms ease",
                      "&:hover": { borderColor: ACCENT, color: ACCENT },
                    }}
                  >
                    <AddAPhotoOutlined fontSize="small" />
                    Add photo
                    <input
                      type="file"
                      accept="image/jpeg,image/png,video/mp4"
                      multiple
                      hidden
                      onChange={handleAddFiles}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Action bar: stays just above the bottom tabs */}
      <Box
        sx={{
          position: "sticky",
          bottom: "calc(60px + env(safe-area-inset-bottom))",
          zIndex: 3,
          // "auto" pushes the bar to the bottom of the screen
          mt: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 1, md: 1.5 },
          bgcolor: "#fff",
          borderTop: "1px solid #E4E7EC",
          boxShadow: "0 -4px 12px rgba(16, 24, 40, 0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        {/* Phones: two short lines; desktop: one line with icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          <EventOutlined
            fontSize="small"
            sx={{ display: { xs: "none", sm: "block" } }}
          />
          {/* No delivery date on self orders: the manufacturer confirms it */}
          <Box>
            <Typography
              sx={{ fontSize: 11.5, lineHeight: 1.2, display: { sm: "none" } }}
            >
              Delivery date
            </Typography>
            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.3 }}>
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Delivery date will be{" "}
              </Box>
              <Box component="span" sx={{ display: { sm: "none" } }}>
                Confirmed by manufacturer
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                confirmed by the manufacturer
              </Box>
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            ...primaryButtonSx,
            py: { xs: 1.25, md: 1.5 },
            flex: { xs: 1, sm: "0 0 auto" },
            minWidth: { sm: 240 },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : reorderFrom ? (
            "Place repeat order"
          ) : (
            "Place order"
          )}
        </Button>
      </Box>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerOrderCreate;
